// modernCountryCard.jsx  
import React from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  Box,
  Chip,
  Stack,
  Avatar
} from '@mui/material';
import PublicIcon from '@mui/icons-material/Public';
import { 
  Bar, 
  BarChart, 
  ResponsiveContainer, 
  XAxis, 
  YAxis,
  Tooltip,
  Cell
} from 'recharts';
import { alpha, useTheme } from '@mui/material/styles';

const getCountryFlag = (code) => {
  const flagMap = {
    'US': '🇺🇸', 'CA': '🇨🇦', 'GB': '🇬🇧', 'AU': '🇦🇺', 'DE': '🇩🇪',
    'FR': '🇫🇷', 'IT': '🇮🇹', 'ES': '🇪🇸', 'BR': '🇧🇷', 'MX': '🇲🇽',
    'IN': '🇮🇳', 'JP': '🇯🇵', 'KR': '🇰🇷', 'CN': '🇨🇳', 'NL': '🇳🇱',
  };
  return flagMap[code] || '🌍';
};

const getCountryName = (code) => {
  const nameMap = {
    'US': 'United States', 'CA': 'Canada', 'GB': 'United Kingdom',
    'AU': 'Australia', 'DE': 'Germany', 'FR': 'France', 'IT': 'Italy',
    'ES': 'Spain', 'BR': 'Brazil', 'MX': 'Mexico', 'IN': 'India',
    'JP': 'Japan', 'KR': 'South Korea', 'CN': 'China', 'NL': 'Netherlands',
  };
  return nameMap[code] || code;
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
        <Box
          sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.98)',
          border: '1px solid #E5E7EB',
          borderRadius: 2,
          p: 1.5,
          boxShadow: '0 8px 20px rgba(15,23,42,0.08)',
        }}
      >
        <Typography variant="body2" fontWeight={600}>
          {getCountryFlag(label)} {getCountryName(label)}
        </Typography>
        <Typography variant="body2" sx={{ color: '#111827' }} fontWeight={700}>
          {payload[0].value.toFixed(1)}%
        </Typography>
      </Box>
    );
  }
  return null;
};

export default function ModernCountryCard({ data = [] }) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  if (!data || data.length === 0) return null;

  const processedData = data.slice(0, 6);
  const colors = ['#3B82F6', '#60A5FA', '#93C5FD', '#BFDBFE', '#CBD5E1', '#E2E8F0'];

  return (
    <Card 
      elevation={0}
      sx={{ 
        height: '100%',
        borderRadius: 3,
        border: `1px solid ${isDarkMode ? '#2A2A2A' : '#E5E7EB'}`,
        backgroundColor: isDarkMode ? '#1A1A1A' : '#FFFFFF',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: isDarkMode
            ? '0 8px 20px rgba(0,0,0,0.3)'
            : '0 8px 20px rgba(15,23,42,0.06)',
        }
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar
            sx={{
              width: 34,
              height: 34,
              backgroundColor: isDarkMode ? 'rgba(255,255,255,0.06)' : '#F3F4F6',
              color: isDarkMode ? '#D1D5DB' : '#6B7280',
              mr: 2,
            }}
          >
            <PublicIcon sx={{ fontSize: 16 }} />
          </Avatar>
          <Typography 
            variant="h6" 
            fontWeight={700}
            color="text.primary"
          >
            Top Countries
          </Typography>
        </Box>

        <Box sx={{ height: 220, mb: 2 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart 
              data={processedData} 
              layout="vertical"
              margin={{ top: 8, right: 6, bottom: 0, left: 10 }}
            >
              <XAxis 
                type="number" 
                domain={[0, 'dataMax']}
                axisLine={false}
                tickLine={false}
                tick={false}
              />
              <YAxis 
                dataKey="code" 
                type="category" 
                axisLine={false}
                tickLine={false}
                width={28}
                tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar 
                dataKey="value" 
                radius={[0, 6, 6, 0]}
                maxBarSize={24}
              >
                {processedData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={colors[index]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {processedData.slice(0, 3).map((country, index) => (
            <Chip
              key={country.code}
              label={`${country.code} ${country.value.toFixed(0)}%`}
              sx={{
                backgroundColor: isDarkMode ? 'rgba(255,255,255,0.06)' : '#F8FAFC',
                color: isDarkMode ? '#FFFFFF' : '#111827',
                fontWeight: 600,
                border: `1px solid ${isDarkMode ? '#343434' : '#E5E7EB'}`,
                height: 34,
                '& .MuiChip-label': {
                  px: 1.25,
                  fontSize: '0.85rem',
                },
              }}
            />
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}
